import { float32Eq, isNumber, isString, present } from '@art-suite/art-core-ts';

import { NonNullableScanValueType, ScanInlineProduct, ScanValueType } from '../CommonScanSchemas';
import { getBooleanScanValueOrUndefined, getEffectiveScanValueAsNumber, ScanValue } from '../ScanRoot';
import {
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from '../ScanRootOptimized';
import {
  getTaskTemplatesForGroup,
  ScanTemplateLineItem,
  ScanTemplateTask,
  ScanTemplateValue,
} from '../ScanTemplateRoot';

// Re-export scoping functions for backwards compatibility
export {
  getAllScopedTasks,
  getScanPartIsScoped,
  hasGroupsScopedAfter,
  isGroupScoped,
  isGroupShownOnScopeReport,
  isLineItemScoped,
  isOptionScoped,
  isScanPartScoped,
  isScanRootScoped,
  isTaskScoped,
  isTaskShownOnScopeReport,
  scanPartHasValues,
} from './scopingLib';

/**
 * @param scanPart
 * @returns the total cost of the scan part if it is defined, otherwise undefined
 */
export const getTotalCostIfDefined = (
  scanPart: ScanPartOptimized | undefined | null,
): number | undefined => {
  if (!scanPart || !scanPart.optimized) return undefined;
  const { materialCost, laborCost } = scanPart.optimized.reducedValues;
  if (materialCost != null && laborCost != null) return materialCost + laborCost;
  if (materialCost != null) return materialCost;
  if (laborCost != null) return laborCost;
  return undefined;
};

export const getTotalRoomAreaIfDefined = (
  scanPart: ScanPartOptimized | undefined | null,
): number | undefined => {
  if (!scanPart || !scanPart.optimized) return undefined;
  return scanPart.optimized.reducedValues.roomArea;
};

/**
 * True when the template value is one the calculation engine can treat as a number: every value type
 * except `text` and `checkbox`, whether the template supplies a literal or an expression.
 */
export const isNumberScanValue = (valueTemplate: ScanTemplateValue): boolean => valueTemplate.type !== 'text' && valueTemplate.type !== 'checkbox';

/**
 * Best-effort coercion of a scan value to a number; it never throws. Strings are parsed after stripping `$`
 * and `,`, so pasted currency such as "$1,250.00" round-trips, and a leading number is used even with
 * trailing text ("12abc" is 12, "12.5 sqft" is 12.5). Anything with no leading number, plus NaN, booleans
 * and null, is `undefined`.
 */
export const getNumberScanValueOrUndefined = (value: ScanValueType | undefined): number | undefined => {
  const numberValue = isNumber(value) ? value : isString(value) ? parseFloat(value.replace(/[$,]/g, '')) : undefined;
  return numberValue == null || isNaN(numberValue) ? undefined : numberValue;
};

/**
 * The single definition of "the user overrode this value": both `setValue` and `calculatedValue` are
 * present and differ (float32 tolerance for numbers). Drives override styling and tooltips in the UX.
 * Relies on `calculatedValue` holding the pure template result, stored as a boolean for checkboxes; the
 * calculation engine guarantees both. A `setValue` equal to the calculation is not an override, and
 * `getUpdatedScanValue` clears such a `setValue` on write. Narrows both fields to non-null for the caller.
 */
export const getScanValueHasOverride = (
  value: ScanValue | undefined | null,
): value is ScanValue & { setValue: NonNullableScanValueType; calculatedValue: NonNullableScanValueType } =>
  value != null
  && value.setValue != null && value.calculatedValue != null
  && (isNumber(value.setValue) && isNumber(value.calculatedValue)
    ? !float32Eq(value.setValue, value.calculatedValue)
    : value.setValue !== value.calculatedValue);

const anyValueToStringOrUndefined = (value: ScanValueType | undefined): string | undefined =>
  !present(value)
    ? undefined
    : String(value);

const valuesArePresentAndSame = (value: ScanValueType | undefined, setValue: ScanValueType | undefined): boolean =>
  present(value) && present(setValue)
  && (isNumber(value) && isNumber(setValue) ? float32Eq(value, setValue) : value === setValue);

/**
 * Normalizes user input into a scan value's `setValue` according to the template value's type: text is
 * trimmed, checkboxes become booleans (the string 'false' is false), and numeric types are parsed with
 * `getNumberScanValueOrUndefined` then clamped to the template's `minValue` and `maxValue`.
 * Absent input (`undefined`, `null`, `''`, or a whitespace-only string) clears the override for every
 * template type, before any type-specific handling runs; `0` and `false` are real values and are kept.
 * Input equal to the current `calculatedValue` (float32 tolerance for numbers) also clears the override,
 * so a value only carries a `setValue` when it is a real override. `calculatedValue` is never touched.
 */
export const getUpdatedScanValue = (
  valueTemplate: ScanTemplateValue,
  value: ScanValue | undefined | null,
  setValue: ScanValueType | undefined,
): ScanValue => {
  if (!present(setValue)) return { ...value, setValue: undefined };

  if (isString(setValue)) {
    setValue = setValue.trim();
  }

  if (valueTemplate.type === 'text') {
    setValue = anyValueToStringOrUndefined(setValue);
  } else if (valueTemplate.type === 'checkbox') {
    setValue = getBooleanScanValueOrUndefined(setValue);
  } else if (isNumberScanValue(valueTemplate)) {
    // else is a number or expression
    setValue = getNumberScanValueOrUndefined(setValue);
    if (isNumber(setValue)) {
      if (valueTemplate.minValue != null) setValue = Math.max(valueTemplate.minValue, setValue);
      if (valueTemplate.maxValue != null) setValue = Math.min(valueTemplate.maxValue, setValue);
    }
  } else {
    if (!present(setValue)) setValue = undefined;
  }

  // if the setValue is the same as the calculatedValue, clear the setValue
  if (valuesArePresentAndSame(value?.calculatedValue, setValue)) {
    setValue = undefined;
  }

  return { ...value, setValue };
};

export const getTotalCost = (
  scanPart: ScanPartOptimized | undefined | null,
): number => getTotalCostIfDefined(scanPart) ?? 0;

export const getChildrenTotalCost = (
  childScanParts: ScanPartOptimized[] | undefined | null,
): number => {
  if (!childScanParts) return 0;
  return childScanParts.reduce(
    (acc, childScanPart) => acc + getTotalCost(childScanPart),
    0,
  );
};

const OTHER_TASK_SUBTYPE = 'otherTask';

/**
 * THE test for a non-standard task: its template is the one marked
 * `otherTask`. Never infer it from anything else, a custom label included.
 */
export const isOtherTaskTemplate = (template?: Pick<ScanTemplateTask, 'subtype'>): boolean => template?.subtype === OTHER_TASK_SUBTYPE;

/** Whether a scan task is a non-standard task, by its template ({@link isOtherTaskTemplate}). */
export const isOtherTask = (task?: ScanTaskOptimized): boolean => isOtherTaskTemplate(task?.template);

export const getEffectiveTaskTrade = (
  task: Pick<ScanTaskOptimized, 'trade' | 'template'>,
  fallbackTrade: string = 'general',
): string => task.trade ?? task.template.trade ?? fallbackTrade;

export const getOtherTaskTemplate = (
  scanRoot: ScanRootOptimized,
  group: ScanGroupOptimized,
): ScanTemplateTask | undefined => getTaskTemplatesForGroup(scanRoot.template, group.template).find(template => isOtherTaskTemplate(template));

export const getLineItemUpdatesWithSetSkuId = (
  item: ScanLineItemOptimized,
  skuId: string,
  inlineProduct: ScanInlineProduct,
): Partial<ScanLineItemOptimized> => {
  return {
    skuId,
    values: item.values?.price
      ? {
        price: { setValue: inlineProduct.price },
      }
      : undefined,
    skuDetails: inlineProduct,
  };
};

/** A zero area counts as undefined: an unmeasured room has no area to show. */
export const getAreaIfDefined = (
  group: ScanGroupOptimized,
): number | undefined => getEffectiveScanValueAsNumber(group.values?.roomArea) || undefined;

export const roundToTwoDecimalPlaces = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const getGroupRoomAreaForReport = (
  group: ScanGroupOptimized,
): number | undefined => {
  const area = getEffectiveScanValueAsNumber(group.values?.roomArea);
  return area == null ? undefined : roundToTwoDecimalPlaces(area);
};

/**
 * Get all tasks from all groups indexed by task ID
 * @param scanRoot - The scan root to get tasks from
 * @returns A record mapping task IDs to tasks
 */
export const getAllTasksById = (
  scanRoot: ScanRootOptimized | undefined,
): Record<string, ScanTaskOptimized> => {
  const tasksById: Record<string, ScanTaskOptimized> = {};
  if (scanRoot) {
    for (const group of scanRoot.groups || []) {
      for (const task of group.tasks ?? []) {
        tasksById[task.id] = task;
      }
    }
  }
  return tasksById;
};

export const isLineItemLabor = (lineItem: ScanLineItemOptimized): boolean => !!lineItem.values.laborCost && !lineItem.values.materialCost;

export const isLineItemTemplateLaborOnly = (
  lineItemTemplate: ScanTemplateLineItem,
): boolean =>
  !!lineItemTemplate.values
  && !!lineItemTemplate.values.laborCost
  && !lineItemTemplate.values.materialCost;

/**
 * Get a scan task by its template ID from a scan root and group ID
 * @param scanRoot - The scan root to search
 * @param groupId - The ID of the scan group containing the task
 * @param taskTemplateId - The template ID of the task to find
 * @returns The scan task if found, otherwise undefined
 */
export const getTaskByTemplateId = (
  scanRoot: ScanRootOptimized,
  groupId: string,
  taskTemplateId: string,
): ScanTaskOptimized | undefined => {
  const group = scanRoot.groups.find(group => group.id === groupId);
  if (!group) return undefined;
  return group.tasks.find(task => task.template.id === taskTemplateId);
};

/**
 * Get a scan option by its template ID from a scan root, group ID, and task ID
 * @param scanRoot - The scan root to search
 * @param groupId - The ID of the scan group containing the task
 * @param taskId - The ID of the scan task containing the option
 * @param optionTemplateId - The template ID of the option to find
 * @returns The scan option if found, otherwise undefined
 */
export const getOptionByTemplateId = (
  scanRoot: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionTemplateId: string,
): ScanOptionOptimized | undefined => {
  const task = getTaskByTemplateId(scanRoot, groupId, taskId);
  if (!task) return undefined;
  return task.options.find(option => option.template.id === optionTemplateId);
};
